// =============================================================================
// ui/tabs/ContractLibrary.tsx — Settings → Trade → your own instruments.
// -----------------------------------------------------------------------------
// 🔴 THE GAP THIS CLOSES IS LOST TRADES, NOT A MISSING PANE.
//
// The instrument list is shared and admin-seeded. A trader holding something it
// does not carry has every trade in that instrument REJECTED AT IMPORT — the
// message even says why — and until now there was no screen in either client
// that could add it. The trades were simply absent from the book, and the fix
// was "wait for a developer to write a migration".
//
// ── ⚠ THE FORM ASKS FOR TWO NUMBERS AND STORES THREE ──
//
// A spec is tick size, tick value and $/point, and asking for all three invites
// a set that disagrees with itself. Exchanges publish the first two as one
// sentence — "0.25 index points = $12.50" — so those are what the form takes,
// and $/point is derived and shown before anything is saved. A trader can check
// the derived figure against the page they are reading from.
//
// ── 🚨 TWO WARNINGS THAT ARE NOT DECORATION ──
//
// 1. OVERRIDING an existing symbol re-prices every trade already in the book in
//    that instrument. Nothing errors; the numbers just change.
// 2. A symbol that EXTENDS one already in the list captures its trades. Add
//    `ESH` — mistaking a March contract for an instrument — and every `ESH26`
//    fill resolves to `ESH` at whatever was typed instead of to `ES`. This is
//    the same longest-prefix rule that once priced Micro Silver 5× too high,
//    seen from the other side.
// =============================================================================

import { useMemo, useState } from 'react';
import { useContracts } from '../state/contracts';
import { useSupabase } from '../state/supabase';
import { deleteUserContract, saveUserContract } from '../../io/sync/userContracts';
import {
  SYMBOL_MAX, dollarsPerPointFrom, symbolImpact, validateUserContract,
  type UserContractDraft,
} from '../../selectors/userContracts';
import { money, num } from '../format';

const EMPTY: UserContractDraft = {
  symbol: '', name: '', exchange: '', tickSize: '', dollarsPerTick: '',
};

export function ContractLibrary() {
  const { sb, userId } = useSupabase();
  const { contracts, custom, customError, reload } = useContracts();

  const [draft, setDraft] = useState<UserContractDraft>(EMPTY);
  /** The symbol being edited, or null when adding. Locks the symbol box. */
  const [editing, setEditing] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const set = (patch: Partial<UserContractDraft>): void => {
    setDraft((d) => ({ ...d, ...patch }));
    setProblem(null);
  };

  /**
   * ⚠ Measured against the catalogue MINUS the row being edited. Without that,
   * re-saving an instrument reports that it overrides itself, which is true and
   * useless — and would train a reader to ignore the warning that matters.
   */
  const others = useMemo(
    () => (contracts ?? []).filter((c) => c.symbol !== editing),
    [contracts, editing],
  );
  const impact = useMemo(
    () => symbolImpact(draft.symbol, others), [draft.symbol, others],
  );

  const derived = dollarsPerPointFrom(
    Number(draft.tickSize), Number(draft.dollarsPerTick),
  );

  const submit = async (): Promise<void> => {
    const checked = validateUserContract(draft, {
      existingCount: custom.length, editing: editing !== null,
    });
    if (!checked.ok) { setProblem(checked.error); return; }
    setBusy(true);
    const res = await saveUserContract(sb, userId, checked.contract);
    setBusy(false);
    if (!res.ok) { setProblem(res.error); return; }
    setDraft(EMPTY);
    setEditing(null);
    setProblem(null);
    reload();
  };

  const remove = async (symbol: string): Promise<void> => {
    setBusy(true);
    const res = await deleteUserContract(sb, userId, symbol);
    setBusy(false);
    setConfirming(null);
    if (!res.ok) { setProblem(res.error); return; }
    if (editing === symbol) { setEditing(null); setDraft(EMPTY); }
    reload();
  };

  const removeAll = async (): Promise<void> => {
    setBusy(true);
    for (const c of custom) {
      const res = await deleteUserContract(sb, userId, c.symbol);
      // ⚠ STOPS at the first failure rather than pressing on. Continuing would
      // leave a partly-reset list with nothing saying which rows survived, and
      // the trader would have to compare the screen against a list they no
      // longer have. Pressing the button again finishes the job.
      if (!res.ok) { setBusy(false); setResetting(false); setProblem(res.error); reload(); return; }
    }
    setBusy(false);
    setResetting(false);
    setEditing(null);
    setDraft(EMPTY);
    reload();
  };

  const startEdit = (symbol: string): void => {
    const c = custom.find((x) => x.symbol === symbol);
    if (!c) return;
    setEditing(symbol);
    setProblem(null);
    setDraft({
      symbol: c.symbol,
      name: c.name,
      exchange: c.exchange,
      tickSize: String(c.tickSize ?? ''),
      dollarsPerTick: String(c.dollarsPerTick ?? ''),
    });
  };

  return (
    <section className="card" data-testid="contract-library">
      <h3>Contract Library</h3>
      <p className="muted">
        Instruments you trade that the standard list does not carry — and
        corrections to ones it does. Adding an instrument here is what lets its
        trades import: a file naming a symbol nothing can price is turned away
        row by row, and this is the only place to fix that.
      </p>

      {/* 🚨 THE CUSTOM READ FAILING IS A WRONG NUMBER, NOT A MISSING ONE. The
          app carries on with the standard catalogue, so a trader's overridden
          instrument prices at somebody else's numbers with nothing else on
          screen to say so. */}
      {customError ? (
        <div className="cmp-error" role="alert">{customError}</div>
      ) : null}

      {custom.length === 0 ? (
        <p className="muted">
          You have no custom instruments. Everything is priced from the standard
          list of {num(contracts?.length ?? 0, 0)}.
        </p>
      ) : (
        <div className="ucon-rows">
          {custom.map((c) => (
            <div className="ucon-row" key={c.symbol} data-testid={`ucon-${c.symbol}`}>
              <div className="ucon-id">
                <b>{c.symbol}</b>
                <span className="muted"> · {c.name}{c.exchange ? ` · ${c.exchange}` : ''}</span>
              </div>
              <div className="ucon-nums muted">
                tick {num(c.tickSize, 4).replace(/0+$/, '').replace(/\.$/, '')}
                {' = '}{money(c.dollarsPerTick)}
                {' · '}{money(c.dollarsPerPoint)}/point
              </div>
              {confirming === c.symbol ? (
                <>
                  <button
                    type="button" className="btn danger btn-sm" disabled={busy}
                    onClick={() => { void remove(c.symbol); }}
                  >
                    Remove “{c.symbol}”
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setConfirming(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-sm" onClick={() => startEdit(c.symbol)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setConfirming(c.symbol)}>
                    Remove
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ⚠ THE TWO DELETIONS ARE NOT THE SAME THING, and the difference decides
          whether trades keep a price. Said here rather than in the confirm
          button, because it is what a reader needs BEFORE pressing one. */}
      {custom.length ? (
        <p className="muted ucon-note">
          Removing an instrument the standard list also carries puts the standard
          numbers back. Removing one it does not carry leaves those trades with
          no price at all — they stay in your book, but their excursion figures
          read $0 and they drop out of tick- and R-denominated totals.
        </p>
      ) : null}

      <div className="ucon-form">
        <h4>{editing ? `Edit ${editing}` : 'Add an instrument'}</h4>

        <label>
          <span>Root symbol</span>
          <input
            type="text" maxLength={SYMBOL_MAX} aria-label="Root symbol"
            placeholder="MNQ" value={draft.symbol} disabled={editing !== null}
            onChange={(e) => set({ symbol: e.target.value.toUpperCase() })}
          />
        </label>
        {editing ? (
          <p className="muted ucon-hint">
            The symbol is what links this to your trades, so it cannot be changed
            here. Remove it and add it again under the new symbol.
          </p>
        ) : (
          <p className="muted ucon-hint">
            The root only — <b>MNQ</b>, not MNQZ26. Month codes and exchange
            suffixes are stripped from your file before it is matched.
          </p>
        )}

        <label>
          <span>Name</span>
          <input
            type="text" aria-label="Instrument name" placeholder="Micro E-mini Nasdaq-100"
            value={draft.name} onChange={(e) => set({ name: e.target.value })}
          />
        </label>

        <label>
          <span>Exchange <span className="muted">(optional)</span></span>
          <input
            type="text" aria-label="Exchange" placeholder="CME"
            value={draft.exchange}
            onChange={(e) => set({ exchange: e.target.value.toUpperCase() })}
          />
        </label>

        <label>
          <span>Tick size</span>
          <input
            type="text" inputMode="decimal" aria-label="Tick size" placeholder="0.25"
            value={draft.tickSize} onChange={(e) => set({ tickSize: e.target.value })}
          />
        </label>

        <label>
          <span>One tick is worth</span>
          <input
            type="text" inputMode="decimal" aria-label="Tick value" placeholder="0.50"
            value={draft.dollarsPerTick}
            onChange={(e) => set({ dollarsPerTick: e.target.value })}
          />
        </label>

        {/* ⚠ SHOWN BEFORE SAVING, because it is the number that rewrites every
            P&L, R-multiple and excursion figure for this instrument and it is
            the one the trader did not type. Checking it against the exchange's
            page is the only review this spec ever gets. */}
        <p className="ucon-derived" data-testid="ucon-derived">
          {derived === null ? (
            <span className="muted">
              Enter a tick size and what one tick is worth, and the value per
              point appears here.
            </span>
          ) : (
            <>
              That makes one full point <b>{money(derived)}</b>. Check it against
              your exchange’s contract specs before saving — a wrong value here
              does not error, it just puts the wrong money on every trade in this
              instrument.
            </>
          )}
        </p>

        {/* 🚨 WARNING 1 — this edits an instrument that already prices trades. */}
        {impact.overrides ? (
          <p className="ucon-warn" role="status">
            ⚠ <b>{impact.overrides}</b> is already in the standard list. Saving
            this replaces it <b>for you only</b>, and re-prices every trade you
            already hold in it.
          </p>
        ) : null}

        {/* 🚨 WARNING 2 — the longest-prefix rule, seen from the adding side. */}
        {impact.captures.length ? (
          <p className="ucon-warn" role="status">
            ⚠ <b>{draft.symbol}</b> starts with{' '}
            <b>{impact.captures[0]}</b>, which is already in the list. Symbols
            beginning “{draft.symbol}” — like {draft.symbol}Z26 — currently price
            as {impact.captures[0]} and would price as {draft.symbol} instead.
            If you meant a delivery month rather than an instrument, this is not
            the screen for it.
          </p>
        ) : null}

        {problem ? <div className="cmp-error" role="alert">{problem}</div> : null}

        <div className="ucon-actions">
          <button
            type="button" className="btn primary" disabled={busy}
            onClick={() => { void submit(); }}
          >
            {busy ? 'Saving…' : editing ? `Save ${editing}` : '+ Add instrument'}
          </button>
          {editing ? (
            <button
              type="button" className="btn"
              onClick={() => { setEditing(null); setDraft(EMPTY); setProblem(null); }}
            >
              Cancel
            </button>
          ) : null}
        </div>
      </div>

      {custom.length ? (
        <div className="ucon-reset">
          {resetting ? (
            <>
              <button
                type="button" className="btn danger btn-sm" disabled={busy}
                onClick={() => { void removeAll(); }}
              >
                Remove all {num(custom.length, 0)} and use the standard list
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setResetting(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-sm" onClick={() => setResetting(true)}>
              Reset to the standard list
            </button>
          )}
        </div>
      ) : null}
    </section>
  );
}
